import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Upload, FileSpreadsheet, Download, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { showToast } from '../utils/toast';
import { handleAPIError } from '../utils/errorHandler';
import { baseUrl } from '../Services/apiService';
import axios from 'axios';
import * as XLSX from 'xlsx';

const BulkUploadModal = ({ open, onClose, onSuccess }) => {
  const { user: currentUser } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      showToast.error('Only .xlsx files are supported');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file size (max 10MB) - consistent with all file uploads
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      showToast.error(`File size must be less than 10MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
    setUploadResult(null);
    setUploadProgress(0);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showToast.error('Please select a file first');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`${baseUrl}/users/bulk-upload`, formData, {
        headers: {
          'Authorization': `Bearer ${currentUser.token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      const result = response.data;
      setUploadResult(result);

      // Show toast based on result
      if (result.success > 0 && result.failed === 0) {
        showToast.success(`✅ ${result.success} users added successfully!`);
        onSuccess && onSuccess();
      } else if (result.success > 0 && result.failed > 0) {
        showToast.warning(`⚠️ ${result.success} added, ${result.failed} failed`);
        onSuccess && onSuccess();
      } else {
        showToast.error(`❌ Upload failed: ${result.failed} errors`);
      }

      // Auto-download error sheet if errors exist
      if (result.failed > 0 && result.errors && result.errors.length > 0) {
        setTimeout(() => downloadErrorSheet(result.errors), 500);
      }

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to upload file';
      handleAPIError(error, 'Failed to upload file', true);
      setUploadResult({ error: true, message: errorMessage });
    } finally {
      setIsUploading(false);
    }
  };

  const downloadErrorSheet = (errors) => {
    try {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const originalData = XLSX.utils.sheet_to_json(firstSheet);

          // Group errors by row
          const errorsByRow = {};
          errors.forEach(error => {
            if (!errorsByRow[error.row]) {
              errorsByRow[error.row] = [];
            }
            errorsByRow[error.row].push(`${error.field}: ${error.message}`);
          });

          // Create error sheet with ONLY invalid rows
          const errorSheetData = originalData.map((row, index) => {
            const rowNumber = index + 2;
            const rowErrors = errorsByRow[rowNumber];
            
            if (rowErrors) {
              return {
                'Full Name': row['Full Name'] || '',
                'Email': row['Email'] || '',
                'Phone Number': row['Phone Number'] || '',
                'Role': row['Role'] || '',
                '❌ ERRORS': rowErrors.join(' | ')
              };
            }
            return null;
          }).filter(row => row !== null);

          // Create workbook
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(errorSheetData);
          ws['!cols'] = [
            { wch: 20 }, // Full Name
            { wch: 35 }, // Email
            { wch: 18 }, // Phone Number
            { wch: 15 }, // Role
            { wch: 50 }  // Errors
          ];
          XLSX.utils.book_append_sheet(wb, ws, 'Fix Errors & Re-upload');

          // Download
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
          XLSX.writeFile(wb, `Errors_${timestamp}.xlsx`);

          
        } catch (parseError) {
          console.error('Parse error:', parseError);
        }
      };

      if (selectedFile) {
        reader.readAsArrayBuffer(selectedFile);
      }
    } catch (error) {
      console.error('Error sheet generation failed:', error);
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      { 'Full Name': 'John Doe', 'Email': 'john@example.com', 'Phone Number': '9876543210', 'Role': 'Employee' },
      { 'Full Name': 'Jane Smith', 'Email': 'jane@example.com', 'Phone Number': '9123456789', 'Role': 'Business Head' },
      { 'Full Name': 'Mike Johnson', 'Email': 'mike@example.com', 'Phone Number': '7654321098', 'Role': 'Vendor' }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [{ wch: 20 }, { wch: 35 }, { wch: 18 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'Bulk_Upload_Template.xlsx');
    showToast.success('📥 Template downloaded');
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[520px] w-[95vw] h-fit overflow-y-auto ">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-yellow-600" />
            Bulk Upload Users
          </DialogTitle>
          <DialogDescription>
            Upload an Excel file to create multiple users at once
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Template Download */}
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">Need a template?</p>
              <p className="text-xs text-blue-700">Download the Excel template to get started</p>
            </div>
            <Button onClick={downloadTemplate} variant="outline" size="sm" className="ml-3">
              <Download className="w-4 h-4 mr-1" />
              Template
            </Button>
          </div>

          {/* File Upload */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Choose Excel File (.xlsx)</label>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleFileSelect}
              disabled={isUploading}
              className="cursor-pointer file:cursor-pointer file:mr-4 file:py-auto file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100"
            />
            
            {selectedFile && (!uploadResult || uploadResult.error) && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg animate-in fade-in slide-in-from-top-2 duration-300">
                <FileSpreadsheet className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-green-900 truncate">{selectedFile.name}</p>
                  <p className="text-xs text-green-700">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                </div>
                <Button onClick={handleReset} variant="ghost" size="sm" disabled={isUploading} className="h-8 w-8 p-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Upload Progress */}
            {isUploading && (
              <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Uploading...</span>
                  <span className="font-medium text-blue-600">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Upload Result */}
          {uploadResult && !uploadResult.error && (
            <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 bg-gray-50 rounded-lg text-center border">
                  <p className="text-xl font-bold text-gray-900">{uploadResult.total || 0}</p>
                  <p className="text-xs text-gray-600">Total</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center border border-green-200">
                  <p className="text-xl font-bold text-green-700">{uploadResult.success || 0}</p>
                  <p className="text-xs text-green-700">Added</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center border border-red-200">
                  <p className="text-xl font-bold text-red-700">{uploadResult.failed || 0}</p>
                  <p className="text-xs text-red-700">Failed</p>
                </div>
              </div>

              {/* Success Message */}
              {uploadResult.success > 0 && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900">
                      {uploadResult.success} user{uploadResult.success > 1 ? 's' : ''} added successfully
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {uploadResult.failed > 0 && (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-900">
                      {uploadResult.failed} row{uploadResult.failed > 1 ? 's' : ''} had errors
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Error sheet downloaded. Fix and re-upload.
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      <span className="font-bold">Note:</span> Before re-uploading the sheet, please delete the <span className="font-bold">X Errors</span> column.
                    </p>
                    <Button
                      onClick={() => downloadErrorSheet(uploadResult.errors)}
                      variant="outline"
                      size="sm"
                      className="mt-2 text-yellow-700 border-yellow-300 hover:bg-yellow-100 h-8"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download Again
                    </Button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button onClick={handleClose} variant="outline" className="flex-1">
                  Close
                </Button>
                {uploadResult.failed > 0 && (
                  <Button onClick={handleReset} className="flex-1">
                    Upload Fixed File
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer - Upload Button */}
        {(!uploadResult || uploadResult.error) && (
          <DialogFooter>
            <Button onClick={handleClose} variant="outline" disabled={isUploading}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!selectedFile || isUploading} className="min-w-[120px]">
              {isUploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Uploading
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload File
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkUploadModal;
